import React, { useState, useEffect } from "react";
import { useCurrentPolicyContext } from "../../actions/CurrentPolicyContext";
import { useLoadingContext } from "../../Loading/LoadingContext";
import { getSingleData } from "../../Utils/CallApiUtils";
import { convertDateFormatWithOutTime } from "../../Utils/commonFunction";
import UserSetting from "../../UserSettings.json";
import DetailCard, { statusTone, formatINR } from "../../Common/DetailCard";
import EmptyState from "../../Common/EmptyState";
import "../Courier/DetailSheet.css";

export default function Claim() {
  const { currentSelectPolicyID, currentSelectPolicyNo } =
    useCurrentPolicyContext();
  const [claimData, setClaimData] = useState([]);
  const { setIsLoading } = useLoadingContext();

  useEffect(() => {
    if (currentSelectPolicyID > 0) {
      getClaimDetail();
    } else {
      window.location = "/";
    }
  }, [currentSelectPolicyID]);

  const getClaimDetail = async () => {
    await getSingleData(
      `InsuranceClaim/AllClaimDetailModel?PolicyID=${currentSelectPolicyID}`,
      setClaimData,
      setIsLoading
    );
  };

  /* The money line is identical on a claim and on each of its additional
     claims, so it is built once. */
  const settlementFacts = (row) => [
    { label: "Claim amount", value: formatINR(row.claimAmount) },
    { label: "Deduction", value: formatINR(row.decutAmount) },
    { label: "Payable", value: formatINR(row.payableAmount), amount: true },
    {
      /* tpaFileDate, not fileDate. The API has always sent TPAFileDate -- which
         arrives camelCased as tpaFileDate -- and this read row.fileDate, which
         is undefined on every claim and every sub-claim there has ever been.

         It never errored because `undefined && ...` is falsy, so the card drew
         the same em dash it draws for a claim genuinely not yet filed. A missing
         field and an empty one looked identical, which is why this survived: the
         screen was not blank, it was confidently reporting "no file date". */
      label: "File date",
      value: row.tpaFileDate && convertDateFormatWithOutTime(row.tpaFileDate),
    },
    { label: "UTR", value: row.utr },
    {
      label: "Payment date",
      value: row.date && convertDateFormatWithOutTime(row.date),
    },
  ];

  if (claimData.length === 0) {
    return (
      <EmptyState
        icon="fa-clipboard-check"
        title="No claims on this policy"
        text="If you raise a claim, you'll be able to follow it here from intimation through to settlement."
        meta={currentSelectPolicyNo && `Policy Number : ${currentSelectPolicyNo}`}
      />
    );
  }

  return (
    <div className="sheet">
      {claimData.map((item, index) => (
        <DetailCard
          key={item.insuranceClaimID || index}
          eyebrow="Patient Name"
          /* The patient leads. On a health claim that is who the customer is
             actually thinking about; the claim numbers identify it. */
          title={item.patientName || "Claim"}
          meta={`Policy Number : ${item.policyNo || currentSelectPolicyNo || "—"}`}
          chip={item.intimationStatusName}
          chipTone={statusTone(item.intimationStatusName)}
          facts={[
            /* Both numbers, because they are not interchangeable: the office
               quotes ours, the insurer quotes theirs, and a customer on the
               phone to either one needs whichever that side recognises. */
            { label: "Our claim number", value: item.ourClaimNumber },
            { label: "Company claim number", value: item.companyClaimNumber },
            { label: "Hospital", value: item.hospitalName },
            {
              label: "Date of admission",
              value:
                item.dateOfAdmission &&
                convertDateFormatWithOutTime(item.dateOfAdmission),
            },
            { label: "Ailment", value: item.ailment },
            /* An address reads badly squeezed into a 168px column. */
            { label: "Hospital address", value: item.hospitalAddress, wide: true },
            ...settlementFacts(item),
          ]}
          documentHref={item.imagePath && UserSetting.imgURL + item.imagePath}
          /* The document attached to a claim is the settlement letter, so it is
             named as one — "Claim document" gave no clue what would open. */
          documentLabel="Settlement Letter"
        >
          {/* Additional claims, nested inside their parent rather than sitting
              loose in the footer — the indent and the tint are what say these
              belong to the claim above. */}
          {item.subClaim && item.subClaim.length > 0 && (
            <>
              <p className="sheet__sublabel">
                {item.subClaim.length} additional{" "}
                {item.subClaim.length === 1 ? "claim" : "claims"}
              </p>
              {item.subClaim.map((Subitem, subIndex) => (
                <DetailCard
                  /* The SUB-claim's own id. insuranceClaimID on a sub-claim is
                     its PARENT's, so a claim with three additional claims gave
                     all three the same React key -- and React reuses a DOM node
                     across rows that share one, which is how a card ends up
                     showing another card's figures after a refresh. */
                  key={Subitem.subInsuranceClaimID || subIndex}
                  nested
                  title={`Additional claim ${subIndex + 1}`}
                  chip={Subitem.intimationStatusName}
                  chipTone={statusTone(Subitem.intimationStatusName)}
                  facts={settlementFacts(Subitem)}
                  documentHref={
                    Subitem.imagePath && `${UserSetting.imgURL}${Subitem.imagePath}`
                  }
                  documentLabel="Settlement Letter"
                />
              ))}
            </>
          )}
        </DetailCard>
      ))}
    </div>
  );
}
